import {
  Sidebar,
  Menu,
  MenuItem,
  SubMenu,
  menuClasses,
} from 'react-pro-sidebar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState, useEffect } from 'react';
import classes from './NavBar.module.scss';
import NavSubMenu from './NavSubMenu';
import { Input } from 'antd';
import GNESearch from '../search/GNESearch';
import { NUMBER_THREE, NUMBER_ZERO } from 'constants/constants';

const NavBar = ({ items }) => {
  const componentAccessList = useSelector(
    (state) => state?.Auth?.componentAccessList
  );

  const isCustomerHierarchyAuthorised = (item) => {
    return (
      componentAccessList?.includes('Wrapper-Org-Hierarchy') ||
      item.categoryName !== 'Customer Hierarchy'
    );
  };
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(true);
  const [displaySubMenu, setDisplaySubMenu] = useState([]);
  const navigate = useNavigate();

  const wrapperObj = useSelector((state) => state?.Auth?.wrapperObj);
  const categoryData = [...(items || []), ...(wrapperObj || [])];

  const currentRoute = location.pathname;

  const { Search } = Input;
  const [searchText, setSearchText] = useState('');
  const [filteredData, setFilteredData] = useState(categoryData);

  useEffect(() => {
    if (collapsed) {
      setSearchText('');
      setFilteredData(categoryData);
    }
  }, [collapsed]);

  // event listener to collapse sidebar
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  const handleClickOutside = (e) => {
    if (!e.target.closest('.left-side-nav') && !e.target.closest('.menu-btn')) {
      setCollapsed(true);
      setDisplaySubMenu([]);
    }
  };

  const onSearch = (value, _e, info) => {
    const searchValue = value?.trim() || '';

    const isEmptySearch = searchValue.length <= NUMBER_ZERO;

    if (info?.source === 'clear' || isEmptySearch) {
      setFilteredData(categoryData);
      setDisplaySubMenu([]);
      return;
    }
    // Minimum input requirement: at least 3 characters to trigger search
    if (searchValue.length < NUMBER_THREE) {
      return;
    }

    // Filter on categoryData nested item reportlist based on search value.
    const filteredCategoryData = categoryData
      .map((item) => {
        if (item?.reportList) {
          const filteredReportList = item.reportList.filter((report) =>
            report?.reportName
              ?.toLowerCase()
              .includes(searchValue.toLowerCase())
          );
          return { ...item, reportList: filteredReportList };
        }
        return item;
      })
      .filter(
        (item) => item?.reportList && item?.reportList?.length > NUMBER_ZERO
      );

    setFilteredData(filteredCategoryData);
    const nextDisplaySubMenu = filteredCategoryData.map(
      (item) => !!item?.reportList?.length
    );
    setDisplaySubMenu(nextDisplaySubMenu);
  };

  const rootStyles = {
    backgroundColor: 'var(--color-secondary-blue)',
    ['.' + menuClasses.button]: {
      backgroundColor: 'var(--color-secondary-blue)',
      paddingRight: '10px !important',
      paddingLeft: '10px !important',
      margin: '0px 5px',
      '&:hover': {
        backgroundColor: 'var(--color-secondary-blue) !important',
      },
    },
    ['.' + menuClasses.label]: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    ['.' + menuClasses.menuItemRoot]: {
      padding: '10px 5px',
    },
  };

  return (
    <div className='left-side-nav'>
      <Sidebar
        collapsed={collapsed}
        collapsedWidth='56px'
        useBackdrop={true}>
        <Menu
          renderExpandIcon={({ open }) => {
            return (
              !collapsed && (
                <>
                  {open ? (
                    <i
                      className={`${classes['nav-link-icon']} icon icon__menu-collapse`}></i>
                  ) : (
                    <i
                      className={`${classes['nav-link-icon']} icon icon__menu-expand`}></i>
                  )}
                </>
              )
            );
          }}>
          {/* Toggle Menu Icon */}
          <MenuItem
            rootStyles={rootStyles}
            key='menuitem1'>
            <button
              className={classes['menu-btn']}
              onClick={() => toggleSidebar()}>
              {!collapsed ? (
                <div className='navbar-collapse'></div>
              ) : (
                <div className='navbar-expand'></div>
              )}
            </button>
          </MenuItem>
          {/* Search Bar */}
          {!collapsed && (
            <MenuItem
              rootStyles={rootStyles}
              key='menuitem2'>
              <GNESearch
                placeholder={`Search by Name`}
                onSearch={onSearch}
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                className='text text__small'
                onClear={onSearch}
              />
            </MenuItem>
          )}

          {/* Menu items */}
          {filteredData.map((item, index) => {
            // Flat menus items
            return !item.reportList ? (
              isCustomerHierarchyAuthorised(item) && (
                <MenuItem
                  component={<Link to={item.link} />}
                  key={index}
                  className={
                    currentRoute.indexOf(item.link) !== -1 ? 'active' : ''
                  }
                  title={item.categoryName}>
                  <i
                    title={item.categoryName}
                    className={`icon icon__${item.categoryIcon} nav-link-icon`}></i>
                  {!collapsed && item.categoryName}
                </MenuItem>
              )
            ) : (
              // Nested Menu Items with Submenu
              <SubMenu
                key={index}
                title={item.categoryName}
                label={
                  <NavSubMenu
                    item={item}
                    collapsed={collapsed}
                  />
                }
                open={!!displaySubMenu[index]}
                rootStyles={{
                  ['& > .' + menuClasses.button]: {
                    paddingRight: '10px !important',
                    '&:hover': {
                      backgroundColor: 'var(--color-tertiary-blue) !important',
                    },
                  },
                  ['.' + menuClasses.label]: {
                    textOverflow: 'initial',
                  },
                  ['& > .' + menuClasses.icon]: {
                    backgroundImage:
                      "url('./icon_menu_engagement.png') !important",
                    width: '26px',
                    height: ' 26px',
                  },
                }}
                onClick={() => {
                  setDisplaySubMenu((prev) => {
                    const next = [...prev];
                    next[index] = !next[index];
                    return next;
                  });
                }}
                className={
                  currentRoute.indexOf(item.link) !== -1 ? 'active' : ''
                }>
                {displaySubMenu[index] &&
                  item.reportList &&
                  item?.reportList?.map((report, i) => {
                    return (
                      <MenuItem
                        title={report.reportName}
                        onClick={() => {
                          if (report.isExternal === 'Y') {
                            window.open(report.reportURL, '_blank');
                          } else {
                            navigate('/reportDashboard', {
                              state: { url: report.reportURL },
                            });
                          }
                          setDisplaySubMenu([]);
                        }}
                        rootStyles={{
                          ['.' + menuClasses.button]: {
                            backgroundColor: 'var(--color-dark-blue)',
                            '&:hover': {
                              backgroundColor:
                                'var(--color-tertiary-blue) !important',
                            },
                          },
                        }}
                        key={i}>
                        {report.reportName}
                        {report.isExternal === 'Y' ? (
                          <i className='icon icon__external float-right mt-1'></i>
                        ) : (
                          ''
                        )}
                      </MenuItem>
                    );
                  })}
              </SubMenu>
            );
          })}
        </Menu>
      </Sidebar>
    </div>
  );
};

export default NavBar;
